import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  ApiRateLimitStat,
  DailyApiUsage,
} from '@tribeplatform/gql-client/types'
import { useI18n } from '@tribeplatform/react-components/i18n'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { ResourceCardFooter } from '../components/ResourceCardFooter.js'
import { ApiUsageTooltip } from './ApiUsageTooltip.js'
import { dailyUsageToChartDataMapper } from './utils.js'

interface Props {
  loading: boolean
  title: string
  description: string
  syncId: string
  data: DailyApiUsage[]
  consumers: string[]
  colors: Record<string, string>
  stat?: ApiRateLimitStat
  finePrint?: React.ReactNode
}

export const ApiUsageCard = ({
  data,
  title,
  description,
  stat,
  loading,
  syncId,
  consumers,
  colors,
  finePrint,
}: Props) => {
  const limit = stat?.limit

  const { formatDate, formatNumber, $t } = useI18n()

  const chartData = data.map(dailyUsageToChartDataMapper)

  return (
    <Card justifyContent="start">
      <Card.Header
        title={title}
        description={
          <p className="text-sm text-content-muted">{description}</p>
        }
      />
      <Card.Content className="flex-none">
        {loading ? (
          <div className="h-full flex flex-col justify-end">
            <div className="space-y-3">
              <div className="h-5 w-1/2 bg-skeleton animate-pulse rounded"></div>
              <div className="h-2 bg-skeleton animate-pulse"></div>
            </div>
          </div>
        ) : (
          <ResponsiveContainer
            minWidth="100%"
            minHeight={180}
            maxHeight={240}
            aspect={1.2}
          >
            <BarChart
              width={500}
              height={300}
              data={chartData}
              margin={{
                bottom: 10,
                right: 20,
              }}
              syncId={syncId}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                fontSize="0.7rem"
                interval={0}
                axisLine={false}
                stroke="var(--bm-color-content-on-background-subdued)"
                dataKey="date"
                angle={45}
                textAnchor="start"
                tickFormatter={v => {
                  return formatDate(v, {
                    month: 'short',
                    day: 'numeric',
                  })
                }}
              />
              <YAxis
                type="number"
                domain={[0, limit * 1.2]}
                width={35}
                scale="sqrt"
                fontSize="0.7rem"
                stroke="var(--bm-color-content-on-background-subdued)"
                tickSize={2}
                tickFormatter={v => {
                  return formatNumber(v, {
                    notation: 'compact',
                    compactDisplay: 'short',
                  })
                }}
              />
              <Tooltip
                content={ApiUsageTooltip}
                cursor={{
                  stroke: 'var(--bm-color-content-on-background-subdued)',
                  strokeDasharray: '5 5',
                  fill: 'transparent',
                }}
              />
              {consumers.map(key => (
                <Bar
                  activeBar={false}
                  barSize={20}
                  key={key}
                  dataKey={key}
                  stackId="a"
                  fill={colors[key]}
                />
              ))}
              <ReferenceLine
                ifOverflow="extendDomain"
                y={limit}
                isFront
                label={{
                  fontSize: '0.8rem',
                  textAnchor: 'start',
                  dominantBaseline: 'hanging',
                  value: $t(
                    {
                      id: 'Admin.Billing.ApiUsageChart.LimitLabel',
                      defaultMessage: 'Limit ({limit, number, ::compact})',
                    },
                    { limit },
                  ),
                }}
                stroke="var(--bm-color-action-destructive)"
                strokeDasharray="3 3"
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card.Content>
      {finePrint && (
        <ResourceCardFooter>
          <Text as="p" variant="bodySm">
            {finePrint}
          </Text>
        </ResourceCardFooter>
      )}
    </Card>
  )
}
